import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, KeyRound, AlertCircle, ArrowRight, Sun, Moon } from 'lucide-react';
import { Button, Input, Card, CardContent } from '@/components/atoms';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { WEBSITE_BACKEND_URL } from '@/config/backend';

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isLoading, error, clearError } = useAuthStore();
  const { theme, setTheme } = useUIStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [notice, setNotice] = useState('');
  const [showTwoFactor, setShowTwoFactor] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      await login(email, password, twoFactorCode);
      navigate('/');
    } catch {
      // Error is handled by the store
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-surface-50 dark:bg-surface-950">
      {/* Theme Toggle */}
      <button
        onClick={toggleTheme}
        className="absolute top-6 right-6 p-3 rounded-xl bg-white dark:bg-surface-800 
                   text-surface-600 dark:text-surface-400 
                   hover:text-surface-900 dark:hover:text-surface-100
                   shadow-lg border border-surface-200 dark:border-surface-700
                   transition-all duration-300 z-20"
        title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        <div className="relative w-5 h-5">
          <Sun className={`w-5 h-5 absolute inset-0 transition-all duration-300 ${
            theme === 'dark' ? 'opacity-100 rotate-0' : 'opacity-0 rotate-90'
          }`} />
          <Moon className={`w-5 h-5 absolute inset-0 transition-all duration-300 ${
            theme === 'light' ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'
          }`} />
        </div>
      </button>

      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary-500/5 dark:bg-primary-500/10 rounded-full blur-[120px] animate-pulse-soft" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-accent-500/5 dark:bg-accent-500/10 rounded-full blur-[100px] animate-pulse-soft" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary-100/50 dark:bg-primary-900/20 rounded-full blur-[150px]" />
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.02]"
           style={{
             backgroundImage: `linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)`,
             backgroundSize: '50px 50px'
           }} />

      <Card variant="glass" className="w-full max-w-md relative z-10 overflow-hidden">
        {/* Top Glow Line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/50 to-transparent" />
        
        <CardContent className="p-8 md:p-10">
          {/* Logo & Title */}
          <div className="text-center mb-10">
            <h1 className="text-3xl font-display font-bold mb-2 text-surface-1000 dark:text-surface-0">
              PostureVision®
            </h1>
            <span className="admin-badge text-sm px-4 py-1.5">
              {t('common.admin')}
            </span>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-danger-500/10 border border-danger-500/20 rounded-xl flex items-start gap-3 animate-fade-in">
              <AlertCircle className="w-5 h-5 text-danger-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-danger-500">{error}</p>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label={t('auth.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@posture.vision"
              leftIcon={<Mail className="w-4 h-4" />}
              required
            />

            <Input
              label={t('auth.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              leftIcon={<Lock className="w-4 h-4" />}
              required
            />

            {showTwoFactor && (
              <div className="animate-fade-in-up">
                <Input
                  label={t('auth.twoFactor')}
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  placeholder="000000"
                  leftIcon={<KeyRound className="w-4 h-4" />}
                  maxLength={6}
                />
              </div>
            )}

            <div className="flex items-center justify-between text-sm pt-1">
              <label className="flex items-center gap-2.5 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                  />
                  <div className="w-5 h-5 rounded-md bg-surface-100 dark:bg-surface-800 border border-surface-300 dark:border-surface-700 peer-checked:bg-primary-500 peer-checked:border-primary-500 transition-all" />
                  <svg className="absolute top-1 left-1 w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-surface-500 dark:text-surface-400 group-hover:text-surface-700 dark:group-hover:text-surface-300 transition-colors">{t('auth.rememberMe')}</span>
              </label>
              <button
                type="button"
                onClick={async () => {
                  if (!email) { setNotice('Enter your email address first, then click this again.'); return; }
                  setNotice('Sending...');
                  try {
                    const r = await fetch(`${WEBSITE_BACKEND_URL}/password/forgot-password`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email }),
                    });
                    const d = await r.json().catch(() => ({}));
                    // Google-only accounts have no password to reset. Say that
                    // plainly and point at the button that does work, rather
                    // than leaving the click looking broken.
                    setNotice(
                      d?.message ||
                        (r.ok
                          ? 'Check your email for a reset link.'
                          : 'Could not send a reset link. If you signed up with Google, use the Google button below.'),
                    );
                  } catch {
                    setNotice('Could not reach the server. Try again in a moment.');
                  }
                }}
                className="text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors font-medium"
              >
                {t('auth.forgotPassword')}
              </button>
            </div>

            {!showTwoFactor && (
              <button
                type="button"
                onClick={() => setShowTwoFactor(true)}
                className="text-sm text-surface-500 hover:text-surface-700 dark:hover:text-surface-300 transition-colors"
              >
                Have a 2FA code?
              </button>
            )}

            <Button
              type="submit"
              className="w-full mt-6"
              size="lg"
              isLoading={isLoading}
              rightIcon={!isLoading && <ArrowRight className="w-4 h-4" />}
            >
              {isLoading ? t('auth.loggingIn') : t('auth.loginButton')}
            </Button>

            {notice && (
              <p className="mt-4 text-sm text-surface-600 dark:text-surface-300 text-center">{notice}</p>
            )}

            {/* Google sign-in. Admin accounts created through Google have no
                password at all, so email and password alone locked those users
                out completely with no way in. The state param carries this
                origin so the callback returns here rather than to the consumer
                app. */}
            <div className="mt-6 flex items-center gap-3">
              <span className="h-px flex-1 bg-surface-200 dark:bg-surface-700" />
              <span className="text-xs text-surface-400 dark:text-surface-500">or</span>
              <span className="h-px flex-1 bg-surface-200 dark:bg-surface-700" />
            </div>

            <button
              type="button"
              onClick={() => {
                // The auth service builds its own OAuth state and reads the
                // return destination from `origin`, not `state`. Passing
                // `state` here silently fell through to the default frontend,
                // which sent admins into the consumer app after signing in.
                window.location.href =
                  `${WEBSITE_BACKEND_URL}/auth/google?origin=${encodeURIComponent(window.location.origin)}`;
              }}
              className="mt-4 w-full flex items-center justify-center gap-3 rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 px-4 py-3 text-sm font-medium text-surface-700 dark:text-surface-200 hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 01-2.4 3.7v3h3.9c2.3-2.1 3.5-5.2 3.5-8.9z"/>
                <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1A12 12 0 0012 24z"/>
                <path fill="#FBBC05" d="M5.4 14.4a7.2 7.2 0 010-4.6V6.7H1.4a12 12 0 000 10.8l4-3.1z"/>
                <path fill="#EA4335" d="M12 4.8c1.8 0 3.4.6 4.6 1.8l3.5-3.5A12 12 0 001.4 6.7l4 3.1C6.3 6.9 8.9 4.8 12 4.8z"/>
              </svg>
              Continue with Google
            </button>
          </form>

        </CardContent>
      </Card>

      {/* Footer */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-surface-400 dark:text-surface-600 text-xs">
        © {new Date().getFullYear()} Posture Vision Corporation. All rights reserved.
      </div>
    </div>
  );
};

export default LoginPage;
